import React from 'react';
import { useInView } from 'react-intersection-observer';
import './Polymathy.css';

const Polymathy = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const triadModel = [
    {
      component: 'IQ (Intelligence Quotient)',
      analogy: 'The Engine Capacity',
      description: 'Cognitive potential; the raw power for reasoning, analysis, and deep learning.',
      contribution: 'Depth & Complexity: Allows for mastery of complex subjects within each domain of study.',
      icon: 'fas fa-brain'
    },
    {
      component: 'CQ (Curiosity Quotient)',
      analogy: 'The Exploration Drive',
      description: 'The intrinsic motivation and thirst to seek, question, and explore new knowledge.',
      contribution: 'Breadth & Integration: Provides the impulse to cross disciplinary boundaries and seek connections between fields.',
      icon: 'fas fa-lightbulb'
    },
    {
      component: 'Polymathy (The Outcome)',
      analogy: 'The Journey\'s Map',
      description: 'The resulting wide-ranging knowledge, diverse skill set, and integrative thinking.',
      contribution: 'Adaptability & Innovation: The ability to draw on varied expertise to solve novel, complex problems.',
      icon: 'fas fa-compass'
    }
  ];

  const directionalFlow = [
    'High CQ compels the individual to engage with a new field of study.',
    'IQ facilitates the rapid and deep acquisition of knowledge in that field.',
    'Successful acquisition reinforces CQ, encouraging the individual to seek out the next new field, leading to an ever-expanding body of knowledge across disciplines.'
  ];

  return (
    <section id="polymathy" className="polymathy-section" ref={ref}>
      <div className="container">
        {/* The Concept - Three Components */}
        <div className="concept-section">
          <h3 className="subsection-title">The Concept</h3>
          
          {/* IQ Section */}
          <div className={`concept-card ${inView ? 'fade-in' : ''}`}>
            <div className="concept-header">
              <div className="concept-icon">
                <i className="fas fa-brain"></i>
              </div>
              <h4 className="concept-title">1. Intelligence Quotient (IQ)</h4>
            </div>
            <p className="concept-description">
              IQ is a measure of an individual's cognitive ability, encompassing general intelligence, 
              reasoning, problem-solving, and the capacity to learn quickly. It represents the potential 
              or "hardware" for intellectual achievement.
            </p>
            <div className="concept-details">
              <div className="detail-item">
                <i className="fas fa-cog"></i>
                <div>
                  <strong>Role:</strong> Determines the speed and complexity with which an individual 
                  can acquire and process new information.
                </div>
              </div>
              <div className="detail-item">
                <i className="fas fa-seedling"></i>
                <div>
                  <strong>Nature:</strong> Generally considered a relatively fixed trait that is difficult 
                  to significantly increase after childhood.
                </div>
              </div>
            </div>
          </div>

          {/* CQ Section */}
          <div className={`concept-card ${inView ? 'fade-in' : ''}`} style={{ animationDelay: '0.1s' }}>
            <div className="concept-header">
              <div className="concept-icon">
                <i className="fas fa-lightbulb"></i>
              </div>
              <h4 className="concept-title">2. Curiosity Quotient (CQ)</h4>
            </div>
            <p className="concept-description">
              CQ (or Curiosity Quotient/Audit) is a concept popularized by journalist Thomas L. Friedman 
              and others. It describes an individual's innate drive, passion, and hunger for continuous 
              learning, exploration, and questioning. It represents the drive or "software" that utilizes 
              one's IQ.
            </p>
            <div className="concept-details">
              <div className="detail-item">
                <i className="fas fa-search"></i>
                <div>
                  <strong>Role:</strong> Determines the breadth and intensity of knowledge seeking. 
                  Individuals with high CQ are tolerant of ambiguity, adapt quickly to change, and are 
                  innately motivated to solve problems.
                </div>
              </div>
              <div className="detail-item">
                <i className="fas fa-star"></i>
                <div>
                  <strong>Importance:</strong> Many modern commentators argue that high CQ (often paired 
                  with PQ or Passion Quotient) is more crucial than IQ in the modern, rapidly changing, 
                  and information-rich world, leading to the illustrative idea: <strong>CQ + PQ &gt; IQ</strong>.
                </div>
              </div>
              <div className="detail-item">
                <i className="fas fa-chart-line"></i>
                <div>
                  <strong>Nature:</strong> Unlike IQ, CQ is considered a developable skill that can be 
                  nurtured and improved through conscious effort and an open mindset.
                </div>
              </div>
            </div>
          </div>

          {/* Polymathy Section */}
          <div className={`concept-card ${inView ? 'fade-in' : ''}`} style={{ animationDelay: '0.2s' }}>
            <div className="concept-header">
              <div className="concept-icon">
                <i className="fas fa-graduation-cap"></i>
              </div>
              <h4 className="concept-title">3. Polymathy</h4>
            </div>
            <p className="concept-description">
              Polymathy is the state of being a polymath, an individual whose expertise spans a significant 
              number of different subject areas. The term comes from the Greek <em>polymathes</em>, meaning 
              "having learned much."
            </p>
            <div className="concept-details">
              <div className="detail-item">
                <i className="fas fa-trophy"></i>
                <div>
                  <strong>Role:</strong> It is the achievement, the result of consistently applying 
                  intellectual capacity and curiosity across multiple, often unrelated, domains.
                </div>
              </div>
              <div className="detail-item">
                <i className="fas fa-users"></i>
                <div>
                  <strong>Examples:</strong> Figures like Leonardo da Vinci (art, science, invention) 
                  and Benjamin Franklin (statesman, author, scientist) are classic examples of polymaths.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Synthesis */}
        <div className={`synthesis-section ${inView ? 'fade-in' : ''}`}>
          <h3 className="subsection-title">Synthesis: The Core Relationship</h3>
          <div className="synthesis-card">
            <p className="synthesis-text">
              Polymathy is the natural consequence of a high CQ actively leveraging a sufficient IQ. 
              While high IQ provides the ability to master complex subjects, high CQ provides the 
              necessary fuel and direction to consistently venture into and integrate knowledge from 
              multiple, diverse fields, which is the definition of polymathy.
            </p>
          </div>
        </div>

        {/* The Triad Model */}
        <div className="triad-section">
          <h3 className="subsection-title">The Polymathy Triad Model</h3>
          <p className="triad-subtitle">
            The roles of IQ and CQ as the two primary, necessary ingredients that converge to produce Polymathy
          </p>
          
          <div className="triad-grid">
            {triadModel.map((item, index) => (
              <div 
                key={index} 
                className={`triad-card ${inView ? 'fade-in' : ''}`}
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="triad-icon">
                  <i className={item.icon}></i>
                </div>
                <h4 className="triad-component">{item.component}</h4>
                <p className="triad-analogy">{item.analogy}</p>
                <p className="triad-description">{item.description}</p>
                <div className="triad-contribution">
                  <strong>Contribution to Polymathy:</strong>
                  <p>{item.contribution}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={`polymathy-concept-image ${inView ? 'fade-in' : ''}`}>
          <img 
            src="/Polymath_Concept.jpg" 
            alt="Polymathy Concept Diagram"
            loading="lazy"
          />
        </div>

        {/* Directional Flow */}
        <div className={`flow-section ${inView ? 'fade-in' : ''}`}>
          <h3 className="subsection-title">Directional Flow</h3>
          <p className="flow-intro">
            Polymathy is the result of a positive feedback loop driven by CQ:
          </p>
          <div className="flow-diagram">
            {directionalFlow.map((step, index) => (
              <div key={index} className="flow-step">
                <div className="flow-number">{index + 1}</div>
                <div className="flow-content">
                  <p>{step}</p>
                </div>
                {index < directionalFlow.length - 1 && (
                  <div className="flow-arrow">
                    <i className="fas fa-arrow-down"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="flow-conclusion">
            <p>
              <strong>In this model:</strong> IQ sets the ceiling on how well one can learn, but CQ determines 
              the reach—how many different walls are hit before stopping. To achieve true Polymathy (mastery 
              across many fields), both must be high.
            </p>
          </div>
        </div>

        {/* Resource Link */}
        <div className={`resource-section ${inView ? 'fade-in' : ''}`}>
          <div className="resource-card">
            <i className="fas fa-book-open"></i>
            <h4>Learn More About Famous Polymaths</h4>
            <a 
              href="https://www.iberdrola.com/documents/20125/1597213/Polimatas_famosos_ENG.pdf/ef9f8026-db0d-0cc9-087b-158134755d46?t=1643817650290"
              target="_blank"
              rel="noopener noreferrer"
              className="resource-link"
            >
              View Resource
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Polymathy;

